// One search result — image, spec chips, feature badges and a price rail with
// the per-day engine's numbers. Server component: the CTA is a plain link.
import Image from "next/image";
import Link from "next/link";
import styles from "./vehicle-card.module.css";
import { money, moneyCompact, plural, carAlt } from "./format";
import { SpecGrid } from "./CarSpecs";
import FeatureBadges from "./FeatureBadges";
import { CHANNEL } from "@/constants";

// Keep in sync with slugifyName() in lib/storefront.ts (server side).
const slugify = (s) =>
    String(s ?? "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

export default function VehicleCard({ car, points, dates, currency, catalogue = false, agency = false }) {
    const bd = car.breakdown;
    const symbol = bd?.currency ?? car.currency ?? "€";
    const days = bd?.days ?? car.dateRange ?? 1;
    const perDayNet = bd ? bd.rentalNet / Math.max(1, days) : car.currentTotal / Math.max(1, days);
    const perDayGross = bd ? bd.rentalGross / Math.max(1, days) : car.oldPrice / Math.max(1, days);
    // The chip must describe the same trip-average figures it sits beside, not
    // the best single day (bd.maxPercent) of a blended multi-rate trip.
    const percent = perDayGross > 0 ? Math.round((1 - perDayNet / perDayGross) * 100) : 0;
    const hasDiscount = percent > 0 && perDayGross > perDayNet + 0.004;
    const total = bd?.total ?? car.totalPrice ?? 0;
    const gelTotal = Number(bd?.totalGel ?? car.totalGel ?? 0);
    const code = bd?.currencyCode ?? car.currencyCode ?? "";
    const transfers = (bd?.pickupFee ?? 0) + (bd?.dropoffFee ?? 0);
    const deposit = bd?.deposit ?? Number(car.deposit || 0);
    const q = (s) => String(s ?? "").replace(/ /g, "+");
    // Catalogue (dateless landing) cards link to the model page instead of the
    // booking flow — /info needs real trip dates to price anything.
    // Agency portal books through /agency/book (same shell, their prices, the
    // booking attributed to the company) — never the public /info.
    const href = agency
        ? `/agency/book?vid=${car.vid}&points=${q(points)}&dates=${q(dates)}&currency=${currency}`
        : catalogue
            ? `/cars/${slugify(`${car.makeTitle} ${car.modelTitle}`)}`
            : `/info?vid=${car.vid}&points=${q(points)}&dates=${q(dates)}&channel=${CHANNEL}&currency=${currency}`;
    const fromPerDay = Number(car.fromPerDay || 0) || perDayNet;
    // Agency portal: booked cars stay on the list (greyed, flagged) so an agent
    // can see the whole fleet; the card stops being a link since it can't be sold.
    const booked = agency && Boolean(car.booked);
    const margin = Number(car.agencyMargin || 0);

    const images = Array.isArray(car.images) ? car.images : [];
    const primary = images[0] || car.image;
    const secondary = images.find((im, i) => i > 0 && im && im !== primary);

    return (
        <article className={`${styles.card}${booked ? ` ${styles.cardBooked}` : ""}`}>
            {booked && <span className={styles.bookedFlag}>Booked for these dates</span>}
            <Link href={href} className={styles.media} aria-hidden tabIndex={-1}>
                {primary ? (
                    // unoptimized: small local PNGs — raw serving beats the
                    // optimizer queue (and image quota) for a 160-card list
                    <>
                        <Image
                            src={primary}
                            alt={carAlt(car)}
                            fill
                            className={styles.photo}
                            loading="lazy"
                            unoptimized
                        />
                        {secondary && (
                            // second shot fades in on hover (when the car has one)
                            <Image
                                src={secondary}
                                alt=""
                                aria-hidden
                                fill
                                className={`${styles.photo} ${styles.photoHover}`}
                                loading="lazy"
                                unoptimized
                            />
                        )}
                    </>
                ) : (
                    <span className={styles.noPhoto} aria-hidden>🚗</span>
                )}
                {car?.category?.translation && (
                    <span className={styles.categoryChip}>{car.category.translation}</span>
                )}
                {secondary && <span className={styles.moreShots} aria-hidden>⇆</span>}
            </Link>

            <div className={styles.body}>
                <h3 className={styles.title}>
                    <Link href={href} className={styles.titleLink}>
                        {car.makeTitle} {car.modelTitle}
                    </Link>
                    <span className={styles.year}>{car.year_display}</span>
                    {car.propulsion && <span className={styles.propulsion}>{car.propulsion}</span>}
                </h3>

                <SpecGrid car={car} />

                <FeatureBadges badges={car.badges} max={8} />

                <div className={styles.metaLine}>
                    {deposit > 0 ? (
                        <span className={styles.depositNote}>{moneyCompact(symbol, deposit)} refundable deposit</span>
                    ) : (
                        <span className={styles.noDeposit}>No deposit required</span>
                    )}
                </div>
            </div>

            <div className={styles.priceRail}>
                {catalogue ? (
                    <>
                        {/* dateless: cheapest advertised tier, no totals */}
                        <div className={styles.priceBlock}>
                            <div className={styles.total}>
                                <span className={styles.fromWord}>from</span>
                                <span className={styles.totalNow}>{moneyCompact(symbol, fromPerDay)}</span>
                                <span className={styles.code}>/ day</span>
                            </div>
                        </div>
                        <div className={styles.sub}>
                            <span className={styles.perDayNow}>pick dates to see your total</span>
                        </div>
                        <Link href={href} className={`${styles.cta} book-btn-gtag-search-page`}>
                            View &amp; book
                        </Link>
                    </>
                ) : (
                    <>
                {hasDiscount && (
                    <span className={styles.discountChip}>−{percent}%</span>
                )}
                {/* Price block: the total is the hero, with the GEL equivalent
                    tucked right beneath it (GEL is what a Georgian customer
                    actually pays — shown quietly, and skipped when already on GEL). */}
                <div className={styles.priceBlock}>
                    <div className={styles.total}>
                        <span className={styles.totalNow}>{money(symbol, total)}</span>
                        <span className={styles.code}>{code}</span>
                    </div>
                    {symbol !== "₾" && gelTotal > 0 && (
                        <div className={styles.gelNote}>
                            ≈ {moneyCompact("₾", gelTotal)} <span className={styles.gelCode}>GEL</span>
                        </div>
                    )}
                </div>
                {/* How the total breaks down — per-day rate × trip length, as a
                    quiet chip that reads like a little equation. */}
                <div className={styles.sub}>
                    {hasDiscount && <s className={styles.perDayWas}>{moneyCompact(symbol, perDayGross)}</s>}
                    <span className={styles.perDayNow}>{moneyCompact(symbol, perDayNet)}/day</span>
                    <span className={styles.subX} aria-hidden>×</span>
                    <span className={styles.subDays}>{plural(days, "day")}</span>
                </div>
                {transfers > 0 ? (
                    <div className={styles.feeNote}>
                        incl. <strong>{money(symbol, transfers)}</strong> delivery
                        {!car.atPickupCity && car.carLocation ? ` from ${car.carLocation}` : ""}
                    </div>
                ) : (
                    !car.atPickupCity && <div className={styles.feeFree}>Free delivery</div>
                )}
                {bd?.extraHoursFee > 0 && (
                    <div className={styles.feeNote}>
                        incl. <strong>{money(symbol, bd.extraHoursFee)}</strong> for {plural(bd.extraHours, "extra hour")}
                    </div>
                )}
                {booked ? (
                    // taken for these dates — the commit would refuse anyway
                    <span className={`${styles.cta} ${styles.ctaDisabled}`} aria-disabled="true">
                        Not available
                    </span>
                ) : (
                    <Link href={href} className={`${styles.cta} book-btn-gtag-search-page`}>
                        Book now
                    </Link>
                )}
                {agency && margin > 0 && (
                    <span className={styles.marginNote}>
                        includes your fee of {moneyCompact(symbol, margin)}
                    </span>
                )}
                    </>
                )}
            </div>
        </article>
    );
}
